import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from "axios";

import type {
  ExplorePostpaidAmlRequest,
  ExplorePrepaidAmlRequest,
  ExploreAmlResponse,
  WidgetInitRequest,
  WidgetInitResponse,
  VerifyPaymentRequest,
  VerifyPaymentResponse,
  GetIntentDataRequest,
  GetIntentDataResponse,
  PaymentIntentRequest,
  PaymentIntentResponse,
  GetAmlHistoryRequest,
  GetAmlHistoryResponse,
  GetAmlItemByIdRequest,
  GetAmlItemByIdResponse,
  SendPdfReportRequest,
  DiscardPaymentIntentRequest,
  BaseResponse,
} from "./models";

const DEFAULT_CONFIG: AxiosRequestConfig = {
  baseURL: (import.meta.env.VITE_REACT_APP_AMLBOT_API_URL as string)!,
  withCredentials: true,
};

class AmlbotAPI {
  private readonly theAxios: AxiosInstance;

  private static instance: AmlbotAPI;

  private constructor(config: AxiosRequestConfig) {
    this.theAxios = axios.create(config);
    this.initResponseInterceptors();
  }

  private initResponseInterceptors = (): void => {
    this.theAxios.interceptors.response.use(
      this.handleSuccessResponse,
      this.handleErrorResponse,
    );
  };

  private handleSuccessResponse = (response: AxiosResponse) => response;

  private handleErrorResponse = (error: AxiosError) => Promise.reject(error);

  public static getInstance(
    config: AxiosRequestConfig = DEFAULT_CONFIG,
  ): AmlbotAPI {
    if (!this.instance) {
      this.instance = new AmlbotAPI(config);
    }
    return this.instance;
  }

  private post = <S, T>(
    endpoint: string,
    data: S,
    config?: AxiosRequestConfig<unknown>,
  ) => {
    return this.theAxios
      .post<T>(endpoint, data, config)
      .then((response: AxiosResponse<T>) => response.data);
  };

  // private get = <S, T>(endpoint: string, data: S) => {
  //   return this.theAxios
  //     .get<T>(endpoint, { params: data })
  //     .then((response: AxiosResponse<T>) => response.data);
  // };

  public widgetInit = (data: WidgetInitRequest) => {
    return this.post<WidgetInitRequest, WidgetInitResponse>(
      "/api/widget/init",
      data,
    );
  };

  public paymentIntent = (data: PaymentIntentRequest) => {
    return this.post<PaymentIntentRequest, PaymentIntentResponse>(
      "/api/widget/payment-intent",
      data,
    );
  };

  public explorePostpaid = (data: ExplorePostpaidAmlRequest) => {
    return this.post<ExplorePostpaidAmlRequest, ExploreAmlResponse>(
      "/api/widget/explore-postpaid",
      data,
    );
  };

  public explorePrepaid = (data: ExplorePrepaidAmlRequest) => {
    return this.post<ExplorePrepaidAmlRequest, ExploreAmlResponse>(
      "/api/widget/explore",
      data,
    );
  };

  public verifyPayment = (data: VerifyPaymentRequest) => {
    return this.post<VerifyPaymentRequest, VerifyPaymentResponse>(
      "/api/widget/payment-verify",
      data,
    );
  };

  public getIntentData = (data: GetIntentDataRequest) => {
    return this.post<GetIntentDataRequest, GetIntentDataResponse>(
      "/api/widget/intent-data",
      data,
    );
  };

  public getAmlHistory = (data: GetAmlHistoryRequest) => {
    return this.post<GetAmlHistoryRequest, GetAmlHistoryResponse>(
      "/api/widget/aml-history",
      data,
    );
  };

  public getAmlItemById = (data: GetAmlItemByIdRequest) => {
    return this.post<GetAmlItemByIdRequest, GetAmlItemByIdResponse>(
      "/api/widget/aml-item",
      data,
    );
  };

  public discardPaymentIntent = (data: DiscardPaymentIntentRequest) => {
    return this.post<DiscardPaymentIntentRequest, BaseResponse>(
      "/api/widget/fail-intent",
      data,
    );
  };

  public sendPdfReport = (data: SendPdfReportRequest) => {
    return this.post<SendPdfReportRequest, BaseResponse>(
      "/api/widget/send-pdf-report",
      data,
    );
  };
}

const amlbotAPI = AmlbotAPI.getInstance();

export default amlbotAPI;
